"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Bookmark, Clock } from "lucide-react"; // DollarSign retiré car inutilisé
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getFeaturedJobs } from "../../apis/public";
import Api from "../../utils/Api";
import CompanyLogo from "../CompanyLogo";

const FeaturedJobs = () => {
  // On récupère i18n pour connaître la langue actuelle
  const { t, i18n } = useTranslation("home");
  const navigate = useNavigate();
  const [cities, setCities] = useState({});
  const [bookmarked, setBookmarked] = useState({});
  const [featuredJobs, setFeaturedJobs] = useState([]);

  // 1. Récupération des Jobs
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const data = await getFeaturedJobs();
        setFeaturedJobs(data);
      } catch (error) {
        console.error("Error fetching featured jobs:", error);
      }
    };
    fetchJobs();
  }, []);

  // 2. Récupération des villes (Noms FR et AR)
  useEffect(() => {
    const fetchCityNames = async () => {
      const newCities = {};

      for (const job of featuredJobs) {
        const cityId = job.company?.address?.city;

        // On vérifie si on a un ID valide et si on ne l'a pas déjà chargé
        if (cityId && !isNaN(cityId) && !cities[cityId]) {
          try {
            const res = await Api.get(`/data/cities/${cityId}`);

            // ICI: On stocke les deux versions linguistiques
            newCities[cityId] = {
              fr: res.data.name || res.data.name_fr,
              ar: res.data.name_ar
            };
          } catch (err) {
            console.error("Error fetching city", cityId, err);
          }
        }
      }

      if (Object.keys(newCities).length > 0) {
        setCities(prev => ({ ...prev, ...newCities }));
      }
    };

    if (featuredJobs.length > 0) {
      fetchCityNames();
    }
  }, [featuredJobs]); // On garde cities hors des dépendances pour éviter les boucles, on se base sur featuredJobs

  // 3. Mapping des données pour l'UI
  const jobs = featuredJobs.map(job => {
    const cityId = job.company?.address?.city;
    const isArabic = i18n.language === 'ar';

    // Logique de détermination du nom de la ville
    let locationDisplay = t('loading'); // Valeur par défaut pendant le chargement

    if (cityId && !isNaN(cityId)) {
      if (cities[cityId]) {
        // Si langue Arabe : on essaie AR, sinon fallback sur FR
        // Si autre langue : on essaie FR, sinon fallback sur AR
        if (isArabic) {
          locationDisplay = cities[cityId].ar || cities[cityId].fr;
        } else {
          locationDisplay = cities[cityId].fr || cities[cityId].ar;
        }
      }
    } else {
      // Cas où ce n'est pas un ID (ex: "Remote" écrit en dur ou null)
      locationDisplay = job.company?.address?.city || t('featuredJobs.remote');
    }

    return {
      id: job._id,
      title: job.title?.title || t('featuredJobs.unknownJob'),
      type: job.contractType || "",
      company: job.company?.company_name || t('featuredJobs.unknownCompany'),
      location: locationDisplay, // Utilisation du résultat calculé
      logo: job.company?.company_name ? job.company.company_name.charAt(0).toUpperCase() : "C",
      profile_picture: job.company?.profile_picture,
      companyObj: job.company,
      createdAt: job.createdAt
    };
  });

  const toggleBookmark = (e, id) => {
    e.stopPropagation();
    setBookmarked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getBadgeStyle = (type) => {
    switch (type) {
      case "FULL-TIME": return "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400";
      case "PART-TIME": return "bg-primary-100 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400";
      default: return "bg-primary-100 text-primary-700 dark:bg-primary-500/10 dark:text-primary-100";
    }
  };

  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    // Note: Idéalement, ces chaînes devraient aussi être dans les fichiers de traduction t()
    if (days > 0) return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    if (hours > 0) return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
    if (minutes > 0) return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
    return "À l'instant";
  };

  return (
    <section className="py-24 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('featuredJobs.title')} <span className="text-yellow-500">{t('featuredJobs.titleHighlight')}</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400">{t('featuredJobs.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobs.map((job, idx) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate(`/job-offers/${job.id}`)}
              className="group p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-800/50 hover:shadow-xl hover:border-yellow-500/30 transition-all duration-300 cursor-pointer"
            >
              <div className="flex justify-between items-start mb-6">
                {/* Company Logo/Image */}
                <div className="w-16 h-16 rounded-xl bg-gray-50 dark:bg-gray-700 flex items-center justify-center overflow-hidden border border-gray-100 dark:border-gray-600">
                  <CompanyLogo company={job.companyObj} className="w-full h-full object-cover" />
                </div>
                <button aria-label="Ajouter aux favoris" onClick={(e) => toggleBookmark(e, job.id)}>
                  <Bookmark className={`w-5 h-5 transition-colors ${bookmarked[job.id] ? "fill-yellow-500 text-yellow-500" : "text-gray-400 hover:text-gray-600"}`} />
                </button>
              </div>

              <div className="mb-4">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors line-clamp-1">
                  {job.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-3">
                  {job.company}
                </p>

                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getBadgeStyle(job.type)}`}>
                    {t(`featuredJobs.types.${job.type}`)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700/50">
                {/* Time Ago */}
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Clock size={14} />
                  <span>{getTimeSince(job.createdAt)}</span>
                </div>

                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <MapPin size={14} className="text-yellow-500" />
                  <span className="truncate max-w-[100px]">{job.location}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <button onClick={() => navigate("/job-offers")} className="px-8 py-3 bg-transparent border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-full font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            {t('featuredJobs.viewAll')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedJobs;